/**
 * The cast CONTROL CHANNEL, receiver side: one text frame from the sender → what it says. Entitlement
 * announcements, the sender's favorites and the castProtocol messages (meta / sources / scene / hints) ride
 * the same text frames; each part acts only on a frame that carries it. The channel is untrusted: the byte
 * size is capped BEFORE JSON.parse, and anything malformed is ignored.
 */
import { Engine } from '@syqel/ota-client'
import { setRuntimeEntitled } from '../catalogue'
import { parseCastFavorites, setSenderFavorites } from '../favorites'
import { castOnMessage } from './session'

const CAST_TEXT_MAX_BYTES = 64 * 1024 // cap control-message size BEFORE JSON.parse (wire hygiene)
const castTextEncoder = new TextEncoder() // the cap above is in BYTES — measure, don't count code units

/** One control-channel text frame (from the transport's socket worker). */
export function onControlText(raw: unknown): void {
  // `raw.length` is a UTF-16 CODE-UNIT count, not bytes — a multi-byte payload could be ~3× the
  // intended budget before this ever tripped. Measure the encoded size, and cheap-reject on the
  // code-unit count first (byteLength ≥ length, so anything over the cap in units is over in bytes).
  if (typeof raw !== 'string') return
  if (raw.length > CAST_TEXT_MAX_BYTES || castTextEncoder.encode(raw).byteLength > CAST_TEXT_MAX_BYTES) return
  try {
    const msg = JSON.parse(raw) as { entitled?: boolean; favorites?: unknown }
    if (typeof msg.entitled === 'boolean') setRuntimeEntitled(msg.entitled)
    // the sender's starred worlds, newest first (its own frame from a phone; a field of the desktop's
    // sender-state frame). Only a frame that CARRIES the key changes them — a malformed value is ignored.
    if (msg && typeof msg === 'object' && 'favorites' in msg) {
      const favs = parseCastFavorites(msg.favorites)
      if (favs) setSenderFavorites(favs)
    }
    const cast = Engine.validateCastMessage(msg)
    if (cast.ok) castOnMessage(cast.msg)
  } catch { /* non-JSON control frame — ignore */ }
}
